import { Scissors, Trash2, Undo2 } from 'lucide-react'

import type { Product } from '@/lib/api/product-api'
import { formatVND } from '@/lib/format'
import {
    canSplitMaterial,
    productUnitPriceFor,
    showSplitToggle,
} from '@/lib/material-split'
import { m } from '@/lib/paraglide/messages'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ProductPicker } from '@/components/products/product-picker'

export interface OrderItemDraft {
    /** order_item.id khi sửa đơn (dòng đã lưu). Rỗng cho dòng mới thêm. Dùng làm
     *  line_key để gắn dòng vật tư theo dòng đơn. */
    id?: string
    product_id: string
    product_name: string
    /** Autofilled from picked product; empty until user picks one. */
    unit?: string
    quantity: number
    unit_price: number
    discount_percent: number
    /** Dòng đã tách giá vật tư ra khỏi giá SP (SP price_includes_material). */
    material_price_split?: boolean
    /** SP có cờ price_includes_material → hiện nút tách/gộp. */
    price_includes_material?: boolean
    /** M = giá vật tư/1 đơn vị SP (BE tính). Dùng cho tách: giá = master − M. */
    material_price_per_unit?: number
    /** Đơn giá SP gốc (product.unit_price) chốt khi chọn SP — để reset khi toggle. */
    master_unit_price?: number
    /** Số thứ tự dòng trên bảng hàng hóa (dãy số CHUNG với dòng vật tư) — cho
     *  phép xen kẽ sản phẩm và vật tư đúng thứ tự người bán nhập. Chỉ sống ở
     *  client; gửi lên API dưới dạng `sort_order` đã đánh lại 0..n-1.
     *  Xem `@/lib/order-line-order`. */
    row_seq?: number
}

interface Props {
    item: OrderItemDraft
    index: number
    /** Optional warehouse id — feeds ProductPicker's stock display. */
    warehouseId?: string
    /** Danh sách SP để tra cứu cờ price_includes_material + material_price_per_unit
     *  khi chọn SP / khi hydrate đơn (edit form đã có sẵn products). */
    products?: Product[]
    onChange: (
        index: number,
        field: keyof OrderItemDraft,
        value: string | number | boolean,
    ) => void
    onRemove: (index: number) => void
}

/**
 * OrderItemRow — controlled variant dùng cho edit-order-form (useState parent).
 * Cho RHF FormProvider parent (vd /orders/new), dùng `OrderItemRowRhf` để tránh
 * mất focus do parent `form.watch(...)` re-render mỗi keystroke.
 */
export function OrderItemRow({
    item,
    index,
    warehouseId,
    onChange,
    onRemove,
}: Props) {
    const lineTotal =
        item.quantity * item.unit_price * (1 - item.discount_percent / 100)

    const materialPricePerUnit = item.material_price_per_unit ?? 0
    const masterUnitPrice = item.master_unit_price ?? item.unit_price
    const split = item.material_price_split ?? false
    const canToggle = showSplitToggle(
        item.price_includes_material,
        materialPricePerUnit,
    )
    const splitAllowed = canSplitMaterial(masterUnitPrice, materialPricePerUnit)

    const handleProductChange = (productId: string, product?: Product) => {
        onChange(index, 'product_id', productId)
        if (!product) {
            onChange(index, 'unit', '')
            return
        }
        onChange(index, 'product_name', product.name)
        onChange(index, 'unit', product.unit)
        // Chọn SP → mặc định GỘP: đơn giá = master (đã gồm giá vật tư). Chốt
        // master + M + cờ để nút tách/gộp + reset-from-master hoạt động.
        onChange(index, 'unit_price', product.unit_price)
        onChange(index, 'master_unit_price', product.unit_price)
        onChange(
            index,
            'price_includes_material',
            product.price_includes_material ?? false,
        )
        onChange(
            index,
            'material_price_per_unit',
            product.material_price_per_unit ?? 0,
        )
        onChange(index, 'material_price_split', false)
    }

    // Toggle tách/gộp — RESET đơn giá SP từ master (bỏ qua giá user sửa tay).
    const handleToggleSplit = () => {
        const next = !split
        onChange(
            index,
            'unit_price',
            productUnitPriceFor(masterUnitPrice, materialPricePerUnit, next),
        )
        onChange(index, 'material_price_split', next)
    }

    return (
        <tr className="border-b align-top">
            <td className="p-2">
                <ProductPicker
                    value={item.product_id}
                    onChange={handleProductChange}
                    warehouseId={warehouseId}
                    className="w-full min-w-0"
                />
            </td>
            <td className="p-2 pt-4 text-sm text-muted-foreground whitespace-nowrap">
                {item.unit || '—'}
            </td>
            <td className="p-2">
                <Input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    value={item.quantity}
                    onChange={(e) =>
                        onChange(index, 'quantity', Number(e.target.value))
                    }
                    className="w-full"
                />
            </td>
            <td className="p-2">
                <Input
                    type="number"
                    min={0}
                    step="any"
                    value={item.unit_price}
                    onChange={(e) =>
                        onChange(index, 'unit_price', Number(e.target.value))
                    }
                    className="w-full"
                />
                {canToggle ? (
                    <div className="mt-1">
                        <Button
                            type="button"
                            size="sm"
                            variant={split ? 'secondary' : 'outline'}
                            className="h-7 gap-1 px-2 text-xs"
                            disabled={!split && !splitAllowed}
                            onClick={handleToggleSplit}
                        >
                            {split ? (
                                <Undo2 className="size-3" />
                            ) : (
                                <Scissors className="size-3" />
                            )}
                            {split
                                ? m.order_material_merge_button()
                                : m.order_material_split_button()}
                        </Button>
                        {!split && !splitAllowed ? (
                            <p className="mt-0.5 text-[11px] text-amber-600">
                                {m.order_material_split_negative_hint()}
                            </p>
                        ) : null}
                    </div>
                ) : null}
            </td>
            <td className="p-2">
                <Input
                    type="number"
                    min={0}
                    max={100}
                    value={item.discount_percent}
                    onChange={(e) =>
                        onChange(
                            index,
                            'discount_percent',
                            Number(e.target.value),
                        )
                    }
                    className="w-full"
                />
            </td>
            <td className="p-2 pt-4 text-right font-medium whitespace-nowrap">
                {formatVND(lineTotal)}
            </td>
            <td className="p-2">
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => onRemove(index)}
                    className="text-destructive"
                >
                    <Trash2 className="size-4" />
                </Button>
            </td>
        </tr>
    )
}
